/**
 * BHCR Cart controller.
 *
 * Single source of truth for cart mutations and visual updates:
 *  - Serialized request queue (one cart write in flight at a time) so rapid
 *    add / quantity / remove actions never trigger Shopify's "too many cart
 *    updates" (429) rate limit.
 *  - Instant price / count updates from Cart API JSON, reconciled by the
 *    authoritative section HTML when it arrives.
 *  - Checkout button + empty-state always kept in sync with item_count.
 *
 * Exposes window.BhcrCart (queue + sync) and keeps window.BhcrCartPricing
 * for the helpers consumed by cart.js / cart-drawer.js.
 */
(function () {
  /* ----- Formatting --------------------------------------------------- */

  function groupDigits(cents, precision, thousands, decimal) {
    const parts = (Number(cents) / 100).toFixed(precision).split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  /**
   * Renders cents through Shopify's money format string, the same one the
   * `money` filter uses, so patched prices are indistinguishable from the
   * Liquid rendered ones around them.
   */
  function formatWithShopMoneyFormat(cents, format) {
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return '';

    let value;
    switch (match[1]) {
      case 'amount':
        value = groupDigits(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = groupDigits(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = groupDigits(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = groupDigits(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = groupDigits(cents, 2, "'", '.');
        break;
      default:
        return '';
    }

    return format.replace(match[0], value);
  }

  /**
   * Pass the format the matching Liquid used: totals are rendered with
   * money_with_currency, everything else with money.
   */
  function formatMoney(cents, currency, format) {
    if (!Number.isFinite(Number(cents))) return '';

    const code = currency || window.shopCurrency || window.shopBaseCurrency;
    const formatString = format || window.moneyFormat;

    // Intl derives its convention from the page language, which on the English
    // storefront turned Liquid's "45,00 €" into "€45.00". Prefer the shop's own
    // format string, but only for the base currency: it hard codes that
    // symbol, so a market presenting another currency has to fall back.
    if (formatString && code === window.shopBaseCurrency) {
      const formatted = formatWithShopMoneyFormat(cents, formatString);
      if (formatted) return formatted;
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: code || 'EUR',
      }).format(Number(cents) / 100);
    } catch {
      return (Number(cents) / 100).toFixed(2);
    }
  }

  function setAnimatedText(element, text) {
    if (!element) return;
    const next = String(text ?? '');
    if (!next) return;
    if (window.BhcrDigitAnimate) {
      BhcrDigitAnimate.set(element, next);
    } else {
      element.textContent = next;
    }
  }

  function readElementDisplayText(element) {
    if (!element) return '';
    if (window.BhcrDigitAnimate) {
      return BhcrDigitAnimate.readText(element);
    }
    return element.textContent.trim();
  }

  function resolveCurrency(cartData) {
    if (!cartData) return window.shopCurrency;
    if (typeof cartData.currency === 'string') return cartData.currency;
    if (cartData.currency?.iso_code) return cartData.currency.iso_code;
    return window.shopCurrency;
  }

  function hasCartTotals(cartData) {
    return (
      cartData &&
      cartData.total_price != null &&
      Number.isFinite(Number(cartData.total_price)) &&
      cartData.item_count != null
    );
  }

  /* ----- Header count -------------------------------------------------- */

  function formatCartCountDisplay(itemCount) {
    const n = Math.max(0, Math.floor(Number(itemCount)));
    if (!Number.isFinite(n)) return '0';
    return n >= 100 ? '99+' : String(n);
  }

  function buildCartToggleAriaLabel(toggle, itemCount) {
    const n = Math.max(0, Math.floor(Number(itemCount)));
    if (!Number.isFinite(n)) return toggle?.getAttribute('aria-label') || '';
    const labelBase = toggle?.dataset?.cartAriaLabel || '';
    const countOne = toggle?.dataset?.cartCountAriaOne || '';
    const countOther = toggle?.dataset?.cartCountAriaOther || '';
    const countPart =
      n === 1 && countOne ? countOne : countOther.replace('8675309', String(n));
    if (labelBase && countPart) return `${labelBase}, ${countPart}`;
    return labelBase || countPart || String(n);
  }

  function syncCartCountPillWidth(pill, itemCount) {
    if (!pill) return;
    const n = Math.max(0, Math.floor(Number(itemCount)));
    const display = formatCartCountDisplay(n);
    pill.classList.toggle(
      'bhcr-header__cart-count-pill--compact',
      display.length >= 2 || n >= 10
    );
  }

  function setCartPageTitle(itemCount) {
    const n = Math.max(0, Math.floor(Number(itemCount)));
    if (!Number.isFinite(n)) return;

    const suffixWrap = document.querySelector('[data-cart-page-title-suffix]');
    const countEl = document.querySelector('[data-cart-page-title-count]');

    if (suffixWrap) {
      suffixWrap.hidden = n === 0;
      suffixWrap.classList.toggle('hidden', n === 0);
    }

    if (countEl) {
      setAnimatedText(countEl, String(n));
    }
  }

  function setHeaderCount(itemCount) {
    const n = Number(itemCount);
    if (!Number.isFinite(n)) return;
    const countText = formatCartCountDisplay(n);

    document.querySelectorAll('[data-cart-toggle]').forEach((el) => {
      const countSpan = el.querySelector('.cart-count');
      if (countSpan) setAnimatedText(countSpan, countText);

      el.setAttribute('aria-label', buildCartToggleAriaLabel(el, n));

      const pill = el.querySelector('.bhcr-header__cart-count-pill');
      syncCartCountPillWidth(pill, n);
    });

    setCartPageTitle(n);
  }

  /* ----- Checkout button state ---------------------------------------- */

  function applyCheckoutState(button, enabled, enabledHref, disabledHref) {
    if (!button) return;
    if (enabled) {
      button.classList.remove('btn--disabled');
      button.removeAttribute('aria-disabled');
      button.removeAttribute('tabindex');
      if (enabledHref) button.setAttribute('href', enabledHref);
    } else {
      button.classList.add('btn--disabled');
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('tabindex', '-1');
      if (disabledHref) button.setAttribute('href', disabledHref);
    }
  }

  function setCheckoutEnabled(itemCount) {
    if (itemCount == null) return;
    const enabled = Number(itemCount) > 0;
    const checkoutUrl =
      window.shopCheckoutUrl || `${(window.shopUrl || '').replace(/\/$/, '')}/checkout`;
    const cartUrl = window.routes?.cart_url || '/cart';

    applyCheckoutState(document.getElementById('CartDrawer-Checkout'), enabled, checkoutUrl, '#');
    applyCheckoutState(document.getElementById('checkout'), enabled, checkoutUrl, cartUrl);
  }

  /* ----- Free shipping progress --------------------------------------- */

  // The drawer skips section re-rendering on quantity changes, so the Liquid
  // rendered progress would go stale. Patch it from the same cart totals the
  // prices are patched from. Copy comes from data attributes so all strings
  // stay in the locale files.
  function updateFreeShipping(totalCents) {
    document.querySelectorAll('[data-free-shipping]:not([data-free-shipping-static])').forEach((root) => {
      const threshold = parseInt(root.dataset.freeShippingThreshold, 10);
      if (!Number.isFinite(threshold) || threshold <= 0) return;

      const total = Number(totalCents) || 0;
      const remaining = threshold - total;
      const qualified = remaining <= 0;

      root.classList.toggle('is-qualified', qualified);

      const bar = root.querySelector('[data-free-shipping-bar]');
      if (bar) {
        const percent = Math.max(0, Math.min(100, Math.round((total / threshold) * 100)));
        bar.style.strokeDashoffset = String(100 - percent);
      }

      const text = root.querySelector('[data-free-shipping-text]');
      if (text) {
        text.textContent = qualified
          ? root.dataset.freeShippingQualifiedText
          : (root.dataset.freeShippingRemainingTemplate || '').replace(
              '[amount]',
              formatMoney(remaining)
            );
      }
    });
  }

  /* ----- Price application from JSON ---------------------------------- */

  function apply(cartData) {
    if (!cartData) return;

    const currency = resolveCurrency(cartData);

    if (hasCartTotals(cartData)) {
      const totalText = formatMoney(cartData.total_price, currency, window.moneyWithCurrencyFormat);

      document.querySelectorAll('.cart-drawer__totals-price[data-digit-animate]').forEach((element) => {
        setAnimatedText(element, totalText);
      });
      document.querySelectorAll('.bhcr-cart__totals-price[data-digit-animate]').forEach((element) => {
        setAnimatedText(element, totalText);
      });
      document.querySelectorAll('.cart-drawer__title-count[data-digit-animate]').forEach((element) => {
        setAnimatedText(element, String(cartData.item_count));
      });
      setCartPageTitle(cartData.item_count);
      updateFreeShipping(cartData.total_price);
    }

    if (!Array.isArray(cartData.items)) return;

    syncDrawerQuantityInputs(cartData);
    syncMainCartQuantityInputs(cartData);

    cartData.items.forEach((item, index) => {
      const line = index + 1;

      const drawerUnitPrice = document.querySelector(
        `#CartDrawer-Item-${line} .cart-drawer__item-price[data-digit-animate]`
      );
      if (drawerUnitPrice) {
        setAnimatedText(drawerUnitPrice, formatMoney(item.final_price, currency));
      }

      const cartLinePrice = document.querySelector(`#CartItem-${line} .bhcr-cart__item-line-price`);
      if (cartLinePrice) {
        const lineText = formatMoney(item.final_line_price, currency);
        const animatedTarget =
          cartLinePrice.querySelector('[data-digit-animate]') ||
          (cartLinePrice.hasAttribute('data-digit-animate') ? cartLinePrice : null);
        if (animatedTarget === cartLinePrice) {
          setAnimatedText(animatedTarget, lineText);
        } else if (animatedTarget) {
          setAnimatedText(animatedTarget, lineText);
        }
      }
    });
  }

  function optimisticQuantityChange(line, quantity) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.classList.contains('is-empty')) return;

    let totalCents = 0;
    let itemCount = 0;

    drawer.querySelectorAll('.cart-drawer__item').forEach((row) => {
      const match = row.id.match(/CartDrawer-Item-(\d+)/);
      const rowLine = match ? parseInt(match[1], 10) : 0;
      const unitPrice = parseInt(row.dataset.unitPrice, 10);
      const input = row.querySelector('.quantity__input');
      let qty = parseInt(input?.value, 10) || 0;

      if (rowLine === Number(line)) {
        qty = quantity;
      }

      if (qty <= 0) return;
      if (Number.isFinite(unitPrice)) {
        totalCents += unitPrice * qty;
      }
      itemCount += qty;
    });

    apply({
      total_price: totalCents,
      item_count: itemCount,
      currency: window.shopCurrency,
    });
    setHeaderCount(itemCount);
    setCheckoutEnabled(itemCount);
  }

  /* ----- Section HTML helpers ----------------------------------------- */

  function parseSectionDocument(sectionHtml) {
    if (!sectionHtml) return null;
    return new DOMParser().parseFromString(sectionHtml, 'text/html');
  }

  function applyFooterFromDrawerSection(sectionHtml, scope) {
    const doc = parseSectionDocument(sectionHtml);
    if (!doc) return null;

    const root = scope instanceof Element ? scope : document;
    const sourceCount = doc.querySelector('.cart-drawer__title-count');
    const sourceTotal = doc.querySelector('.cart-drawer__totals-price');
    const targetCount = root.querySelector('.cart-drawer__title-count[data-digit-animate]');
    const targetTotal = root.querySelector('.cart-drawer__totals-price[data-digit-animate]');

    const countText = readElementDisplayText(sourceCount);
    const totalText = readElementDisplayText(sourceTotal);

    if (countText && targetCount) setAnimatedText(targetCount, countText);
    if (totalText && targetTotal) setAnimatedText(targetTotal, totalText);

    // The free shipping indicator sits in the footer but outside the items
    // region, so replacing the items does not carry it across. Add to cart
    // responses describe the added line rather than the cart, so apply() gets
    // no totals to work from either and the indicator would keep whatever it
    // said when the page was rendered. Take the whole element from the freshly
    // rendered section: that also covers the teaser and qualified variants,
    // which differ in markup rather than just text.
    const sourceShipping = doc.querySelector('[data-free-shipping]');
    const targetShipping = root.querySelector('[data-free-shipping]');
    if (sourceShipping && targetShipping) {
      targetShipping.replaceWith(document.importNode(sourceShipping, true));
    }

    const itemCount = countText ? parseInt(countText, 10) : null;
    return { itemCount: Number.isFinite(itemCount) ? itemCount : null, countText, totalText };
  }

  function replaceDrawerItemsSection(sectionHtml, scope) {
    const doc = parseSectionDocument(sectionHtml);
    if (!doc) return null;

    const root = scope instanceof Element ? scope : document;
    const source =
      doc.querySelector('#CartDrawer-CartItems') ||
      doc.querySelector('.cart-drawer__items.js-contents');
    const target =
      root.querySelector('#CartDrawer-CartItems') ||
      root.querySelector('.cart-drawer__items.js-contents');

    if (!source || !target) return null;

    target.innerHTML = source.innerHTML;
    if (window.BhcrDigitAnimate) BhcrDigitAnimate.revealIn(target);
    return target;
  }

  function replaceMainCartItemsSection(sectionHtml) {
    const doc = parseSectionDocument(sectionHtml);
    if (!doc) return null;

    const source = doc.querySelector('#main-cart-items .js-contents') || doc.querySelector('cart-items .js-contents');
    const target = document.querySelector('#main-cart-items .js-contents');

    if (!source || !target) return null;

    target.innerHTML = source.innerHTML;
    if (window.BhcrDigitAnimate) BhcrDigitAnimate.revealIn(target);
    return target;
  }

  function replaceMainCartFooterSection(sectionHtml) {
    const doc = parseSectionDocument(sectionHtml);
    if (!doc) return null;

    const source = doc.querySelector('#main-cart-footer .js-contents');
    const target = document.querySelector('#main-cart-footer .js-contents');

    if (!source || !target) return null;

    target.innerHTML = source.innerHTML;
    if (window.BhcrDigitAnimate) BhcrDigitAnimate.revealIn(target);
    return target;
  }

  function resolveSectionHtml(response, sectionsToRender, sectionId) {
    if (!response?.sections) return null;

    const fromRender = sectionsToRender?.find((section) => section.id === sectionId);
    if (fromRender?.section && response.sections[fromRender.section]) {
      return response.sections[fromRender.section];
    }

    const root = document.getElementById(sectionId);
    const key = root?.dataset?.id;
    if (key && response.sections[key]) return response.sections[key];

    if (sectionId === 'CartDrawer-CartItems' && response.sections['cart-drawer']) {
      return response.sections['cart-drawer'];
    }

    return null;
  }

  function getCartSectionsToRender({ includeFooter = true } = {}) {
    const sections = [];

    if (document.querySelector('cart-drawer')) {
      sections.push({
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer',
        selector: '.cart-drawer__items.js-contents',
      });
    }

    const itemsEl = document.getElementById('main-cart-items');
    const footerEl = document.getElementById('main-cart-footer');

    if (itemsEl) {
      sections.push({
        id: 'main-cart-items',
        section: itemsEl.dataset.id,
        selector: '.js-contents',
      });
    }

    if (footerEl && includeFooter) {
      sections.push({
        id: 'main-cart-footer',
        section: footerEl.dataset.id,
        selector: '.js-contents',
      });
    }

    return sections;
  }

  function getCartSectionApiKeys(options) {
    return [...new Set(getCartSectionsToRender(options).map((section) => section.section))];
  }

  function fetchCartSections(options) {
    const keys = getCartSectionApiKeys(options);
    if (!keys.length) return Promise.resolve(null);

    const url = `${window.routes?.cart_url || '/cart'}?sections=${keys.join(',')}`;
    const fetchSections = () =>
      fetch(url)
        .then((response) => {
          if (response.status === 429) {
            noteRateLimitHit();
            throw new Error('rate_limited');
          }
          return response.json();
        })
        .then((sections) => ({ sections }))
        .catch(() => null);

    if (window.BhcrCart?.enqueue) {
      return window.BhcrCart.enqueue(() => withRetry(fetchSections), 'fetch:sections');
    }

    return fetchSections();
  }

  function syncDrawerEmptyState(cartDrawer, itemCount) {
    if (!cartDrawer || itemCount == null) return;
    const isEmpty = itemCount === 0;
    cartDrawer.classList.toggle('is-empty', isEmpty);
    const drawerItems = cartDrawer.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.classList.toggle('is-empty', isEmpty);
  }

  function syncDrawerQuantityInputs(cartData) {
    if (!Array.isArray(cartData?.items)) return;

    cartData.items.forEach((item, index) => {
      const line = index + 1;
      const input = document.getElementById(`Drawer-quantity-${line}`);
      if (input) {
        input.value = item.quantity;
        input.setAttribute('value', String(item.quantity));
      }
    });
  }

  function syncMainCartQuantityInputs(cartData) {
    if (!Array.isArray(cartData?.items)) return;

    cartData.items.forEach((item, index) => {
      const line = index + 1;
      const input = document.getElementById(`Quantity-${line}`);
      if (input) {
        input.value = item.quantity;
        input.setAttribute('value', String(item.quantity));
      }
    });
  }

  function resolveMainCartSectionHtml(response, sectionsToRender, sectionId) {
    return resolveSectionHtml(response, sectionsToRender, sectionId);
  }

  function syncMainCartDom(response, sectionsToRender) {
    const cartItemsRoot = document.querySelector('cart-items.bhcr-cart');
    if (!cartItemsRoot || !response) return null;

    const itemsHtml = resolveSectionHtml(response, sectionsToRender, 'main-cart-items');
    const footerHtml = resolveSectionHtml(response, sectionsToRender, 'main-cart-footer');
    let itemCount = hasCartTotals(response) ? response.item_count : null;

    if (itemsHtml) {
      replaceMainCartItemsSection(itemsHtml);
    } else if (Array.isArray(response.items)) {
      syncMainCartQuantityInputs(response);
    }

    if (footerHtml) {
      replaceMainCartFooterSection(footerHtml);
    }

    if (itemCount != null) {
      const isEmpty = itemCount === 0;
      cartItemsRoot.classList.toggle('is-empty', isEmpty);
      const emptyState = cartItemsRoot.querySelector('.bhcr-cart__empty');
      if (emptyState) emptyState.toggleAttribute('aria-hidden', !isEmpty);

      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
    }

    return { itemCount, didReplaceHtml: Boolean(itemsHtml || footerHtml) };
  }

  /**
   * Authoritative /cart page update from any cart response (drawer change,
   * add-to-cart, etc.). Replaces items + footer when section HTML is present,
   * otherwise reconciles totals / counts from JSON.
   */
  function syncMainCart(response, sectionsToRender) {
    return syncCartUI(response, sectionsToRender);
  }

  function cartNeedsSectionRefresh(result, response) {
    const hasCartUI = document.querySelector('cart-drawer') || document.getElementById('main-cart-items');
    if (!hasCartUI) return false;
    if (result?.didReplaceHtml) return false;

    if (hasCartTotals(response) && Array.isArray(response?.items)) {
      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems) {
        const domLineCount = mainCartItems.querySelectorAll('.bhcr-cart__item, .cart-item').length;
        if (domLineCount !== response.items.length) return true;
      }
      return false;
    }

    return true;
  }

  /**
   * Single paint path for every cart surface: header count, drawer, /cart page,
   * totals, line prices, checkout state, and empty states.
   */
  function syncCartUI(response, sectionsToRender) {
    if (!response) return null;

    const sections = sectionsToRender || getCartSectionsToRender();
    const cartDrawer = document.querySelector('cart-drawer');
    let itemCount = hasCartTotals(response) ? response.item_count : null;

    const drawerHtml = resolveSectionHtml(response, sections, 'CartDrawer-CartItems');
    if (drawerHtml && cartDrawer) {
      replaceDrawerItemsSection(drawerHtml, cartDrawer);
      const footerMeta = applyFooterFromDrawerSection(drawerHtml, cartDrawer);
      if (itemCount == null) itemCount = footerMeta?.itemCount;
    } else if (cartDrawer && Array.isArray(response.items)) {
      syncDrawerQuantityInputs(response);
    }

    const mainResult = syncMainCartDom(response, sections);
    if (itemCount == null) itemCount = mainResult?.itemCount;

    if (hasCartTotals(response)) {
      apply(response);
      setHeaderCount(response.item_count);
      setCheckoutEnabled(response.item_count);
      itemCount = response.item_count;
    } else if (itemCount != null) {
      setHeaderCount(itemCount);
      setCheckoutEnabled(itemCount);
    }

    syncDrawerEmptyState(cartDrawer, itemCount);

    return {
      itemCount,
      didReplaceHtml: Boolean(drawerHtml || mainResult?.didReplaceHtml),
    };
  }

  function syncDrawerQuantityInput(line, cartData) {
    const item = cartData?.items?.[line - 1];
    if (!item) return;

    const input = document.getElementById(`Drawer-quantity-${line}`);
    if (input) {
      input.value = item.quantity;
      input.setAttribute('value', item.quantity);
    }
  }

  /**
   * Authoritative drawer update from any cart response that carries the
   * `cart-drawer` section (add-to-cart, remove, etc.). Replaces the items
   * list, syncs totals / count / checkout state, and toggles empty state.
   */
  function syncDrawer(cart, response) {
    return syncCartUI(response);
  }

  // Back-compat alias used by older call sites.
  function syncDrawerFromAddResponse(response, scope) {
    return syncDrawer(scope, response);
  }

  /* ----- Serialized request queue ------------------------------------- */

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const taskQueue = [];
  let isProcessing = false;
  let activeTask = null;
  let activePendingRun = null;
  let rateLimitUntil = 0;

  async function waitForRateLimitCooldown() {
    const waitMs = rateLimitUntil - Date.now();
    if (waitMs > 0) await delay(waitMs);
  }

  function noteRateLimitHit() {
    rateLimitUntil = Math.max(rateLimitUntil, Date.now() + 1500);
  }

  function processQueue() {
    if (isProcessing) return;
    const task = taskQueue.shift();
    if (!task) return;
    isProcessing = true;
    activeTask = task;
    activePendingRun = null;

    Promise.resolve()
      .then(async () => {
        await waitForRateLimitCooldown();
        let value = await task.run();
        while (activePendingRun && task.coalesceKey) {
          const nextRun = activePendingRun;
          activePendingRun = null;
          await waitForRateLimitCooldown();
          value = await nextRun();
        }
        return value;
      })
      .then(
        (value) => task.resolve(value),
        (error) => task.reject(error)
      )
      .finally(() => {
        isProcessing = false;
        activeTask = null;
        activePendingRun = null;
        processQueue();
      });
  }

  function enqueue(run, coalesceKey) {
    if (coalesceKey) {
      const queued = taskQueue.find((task) => task.coalesceKey === coalesceKey);
      if (queued) {
        queued.run = run;
        return queued.promise;
      }

      if (activeTask?.coalesceKey === coalesceKey) {
        activePendingRun = run;
        return activeTask.promise;
      }
    }

    const task = { run, coalesceKey };
    task.promise = new Promise((resolve, reject) => {
      task.resolve = resolve;
      task.reject = reject;
    });
    taskQueue.push(task);
    processQueue();
    return task.promise;
  }

  function cartUrl() {
    const url = window.routes?.cart_url || '/cart';
    return url.includes('.js') ? url : `${url.replace(/\/$/, '')}.js`;
  }

  function changeUrl() {
    const url = window.routes?.cart_change_url;
    if (!url) return '/cart/change.js';
    return url.includes('.js') ? url : `${url.replace(/\/$/, '')}.js`;
  }

  async function fetchCartJson(url, options) {
    const response = await fetch(url, options);
    const text = await response.text();
    const trimmed = (text || '').trim();
    const looksJson = trimmed.startsWith('{') || trimmed.startsWith('[');

    if (!looksJson) {
      const rateLimited = response.status === 429 || /too\s*many\s*requests/i.test(trimmed.slice(0, 200));
      const err = new Error(rateLimited ? 'rate_limited' : 'parse_failed');
      err.status = response.status;
      if (rateLimited) noteRateLimitHit();
      throw err;
    }

    return JSON.parse(trimmed);
  }

  async function withRetry(run, { maxAttempts = 3 } = {}) {
    let lastError;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await waitForRateLimitCooldown();
      try {
        return await run();
      } catch (err) {
        lastError = err;
        if (err?.message !== 'rate_limited' || attempt >= maxAttempts - 1) {
          throw err;
        }
        noteRateLimitHit();
        await delay(700 * (attempt + 1));
      }
    }

    throw lastError;
  }

  function changeRequest(payload) {
    return fetchCartJson(changeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  function addRequest(body) {
    const url = window.routes?.cart_add_url || '/cart/add';
    if (body instanceof FormData) {
      return fetchCartJson(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
    }
    return fetchCartJson(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  function change(payload) {
    const coalesceKey = payload.line != null ? `change:${payload.line}` : null;
    return enqueue(() => withRetry(() => changeRequest(payload)), coalesceKey);
  }

  function add(body) {
    return enqueue(() => withRetry(() => addRequest(body)));
  }

  function resync({ delayMs = 0, includeFooter = true } = {}) {
    return enqueue(async () => {
      if (delayMs > 0) await delay(delayMs);
      const cart = await withRetry(() => fetchCartJson(cartUrl()));
      const keys = getCartSectionApiKeys({ includeFooter });
      let sectionsPayload = null;

      if (keys.length) {
        const url = `${window.routes?.cart_url || '/cart'}?sections=${keys.join(',')}`;
        sectionsPayload = await withRetry(() => fetchCartJson(url));
      }

      syncCartUI({
        ...cart,
        ...(sectionsPayload ? { sections: sectionsPayload } : {}),
      });
      return cart;
    }, 'resync:state');
  }

  window.BhcrCart = {
    add,
    change,
    enqueue,
    resync,
    syncDrawer,
    syncMainCart,
    syncCartUI,
    setHeaderCount,
    setCheckoutEnabled,
  };

  window.BhcrCartPricing = {
    apply,
    applyFooterFromDrawerSection,
    cartNeedsSectionRefresh,
    fetchCartSections,
    formatMoney,
    getCartSectionApiKeys,
    getCartSectionsToRender,
    hasCartTotals,
    optimisticQuantityChange,
    replaceDrawerItemsSection,
    replaceMainCartFooterSection,
    replaceMainCartItemsSection,
    setCheckoutEnabled,
    setCartPageTitle,
    setHeaderCount,
    syncCartUI,
    syncDrawer,
    syncDrawerFromAddResponse,
    syncDrawerQuantityInput,
    syncDrawerQuantityInputs,
    syncMainCart,
  };
})();
